import * as Cookies from "js-cookie";
import * as Base64 from "js-base64";
import Axios from "axios";

import { apiDomain } from "env";
import unijsAuth from "@unimed/unijs/auth";

import api from "@/api/portal-beneficiario";
import auth from "./api";
import { storage } from "@/plugins";

import { buscaBeneficiariosUsuario } from "@/services/beneficiario";
import { createUsuario, getParametroLogout } from "@/services/usuario";

const handleError = (response, status = 401) => {
  return response.status === status
    ? "Nome de usuário ou senha incorretos."
    : "Ocorreu um erro inesperado. Tente novamente.";
};

const authenticate = (data, store) => {
  if (data) {
    const token = JSON.parse(Base64.decode(data.access_token.split(".")[1]));

    const oauth = {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      expires_in: token.exp * 1000
    };

    store.commit("portal/setUsuario", {
      id: token.usuario_id,
      nome: token.nome,
      userName: token.user_name
    });
    storage.setItem("authVersion", "uaa2");
    storage.setItem("oauth", JSON.stringify(oauth));

    return Promise.resolve(data);
  }

  return Promise.reject();
};

const buildMenu = (store, product) => {
  if (storage.getItem("authVersion") === "uaa") return;

  const oauth = JSON.parse(storage.getItem("oauth"));

  return Axios.get(auth.menus(product), {
    headers: { authorization: `Bearer ${oauth.access_token}` }
  })
    .then(response => setMenuNew(response, store))
    .catch(err => {
      const errorMessage =
        err.response.status === 400
          ? "Usuário não possui acesso ao produto"
          : handleError(err.response);
      logout(store);

      return Promise.reject(errorMessage);
    });
};

const setMenuNew = response => {
  const data = response.data;

  if (data) {
    const menu = createMenuNew(data || []);

    global.localStorage.menu = JSON.stringify(menu);

    return Promise.resolve();
  }

  return Promise.reject();
};

const createMenuNew = menu => {
  if (menu) {
    return menu.map(item => {
      if (item.modulo) {
        return {
          label: item.modulo.nome,
          module: item.modulo.slug.toUpperCase(),
          initial: item.modulo.nome.charAt(0),
          description: item.modulo.descricao || item.modulo.nome,
          itens: createMenuNew(item.menus),
          operations: item.operacoes,
          slug: item.modulo.slug
        };
      }

      if (item) {
        return {
          label: item.nome,
          itens: createMenuNew(item.menus),
          route: item.route ? item.route : null,
          show: item?.show != null ? item?.show : true
        };
      }
    });
  }
};

const dadosUsuario = ({ pagina = "home", redirect = null }, store, router) => {
  buscaBeneficiariosUsuario()
    .then(beneficiariosUsuario => {
      store.commit("portal/setUsuario", {
        ...store.state.portal.usuario,
        pessoaId: beneficiariosUsuario?.dadosBeneficiarioLogado?.pessoaId
      });
      store.commit(
        "beneficiario/setDadosBeneficiario",
        beneficiariosUsuario?.dadosBeneficiarioLogado
      );
      store.commit(
        "beneficiario/setFamiliaPessoa",
        beneficiariosUsuario?.familiaBeneficiario
      );
      store.commit(
        "beneficiario/setTitularIncapaz",
        beneficiariosUsuario?.titularIncapaz
      );

      if (redirect) {
        router.push({ path: redirect });
      } else {
        router.push({ name: pagina });
      }
    })
    .catch(({ response }) => {
      logout({}, store, router);

      if (response?.data?.message) {
        store.commit("portal/setToast", response.data.message);
      } else {
        store.commit("portal/setToast", `Família do usuário não encontrada`);
      }
    })
    .finally(() => {
      store.commit("portal/setShowLoading", false);
    });
};

function login(
  { unimed, username, password, redirect = null },
  store,
  router,
  product
) {
  const headers = {
    "Content-Type": "application/x-www-form-urlencoded",
    Authorization: `Basic ${Base64.encode("uaa-client:uaa-secret")}`,
    "X-Delegation": "portal-administracao-beneficiario",
    "X-Unimed": unimed
  };
  const data = global.encodeURI(
    `username=${username}&password=${password}&grant_type=password`
  );

  storage.removeItem("oauth");
  storage.setItem("unimed", unimed);

  store.commit("portal/setShowLoading", true);

  return Axios.post(api.token, data, { headers })
    .then(({ data }) => authenticate(data, store))
    .then(response =>
      unijsAuth.checkLGPD.apply(this, [true, response, "portal-beneficiario"])
    )
    .then(() => {
      dadosUsuario({ redirect }, store, router);
    })
    .catch(response => {
      store.commit("portal/setShowLoading", false);

      const status = response?.response?.status;
      if (!status) {
        logout({}, store, router);
        return Promise.reject(response?.response || response);
      }

      const error_description = response.response.data.error_description;
      if (error_description === "User credentials have expired") {
        return Promise.reject(error_description);
      }
      if (error_description === "User is disabled") {
        return Promise.reject(error_description);
      }
      if (status === 400) {
        return Promise.reject(error_description);
      }

      logout({}, store, router);
      return Promise.reject(handleError(response));
    })
    .then(() => buildMenu(store, product));
}

const logout = ({ redirect = null }, store, router) => {
  getParametroLogout(storage.getItem("unimed")).then(url => {
    if (url) {
      window.location.href = url;
    }
  });

  const subDomain = apiDomain("default").split(".")[0];
  const domain = apiDomain("default").replace(subDomain, "");

  Cookies.remove("oauth", { domain: domain });
  storage.removeItem("authVersion");
  storage.removeItem("oauth");
  storage.removeItem("menu");
  store.commit("portal/clearAll");
  store.commit("beneficiario/clearAll");

  if (redirect) {
    router.push({ name: "acessar", query: { redirect } });
  } else {
    router.push({ name: "acessar" });
  }
};

export default {
  alterarSenha(data, store, router) {
    store.commit("portal/setShowLoading", true);

    return Axios.post(api.alterarSenha, {
      senha: data.senha,
      senhaConfirmation: data.senhaConfirmation,
      token: data.token
    })
      .then(() => {
        store.commit("portal/setToast", "Senha redefinida com sucesso");
        router.push({ name: "acessar" });
      })
      .catch(({ response }) => {
        const status = response.status;

        let error_message = null;

        if (!status) {
          error_message = response;
        } else {
          error_message = response.data.message;
        }

        store.commit("portal/setToast", error_message);
      })
      .finally(() => {
        store.commit("portal/setShowLoading", false);
      });
  },
  login,
  loginAutomatico(tokenData, store, router, product) {
    const data = JSON.parse(Base64.decode(tokenData));

    store.commit("portal/setShowLoading", true);

    return authenticate(data, store)
      .then(() => {
        const params = {
          pagina: data.pagina
        };

        dadosUsuario(params, store, router);
      })
      .catch(() => {
        store.commit("portal/setShowLoading", false);
      })
      .then(() => buildMenu(store, product));
  },
  logout,
  primeiroAcesso(data, store, router) {
    store.commit("portal/setShowLoading", true);

    createUsuario(data)
      .then(() => {
        const params = {
          unimed: data.unimed,
          username: data.email,
          password: data.senha
        };

        login.apply(this, [params, store, router]);
      })
      .catch(error => {
        const error_message = error.response.data
          ? error.response.data.message
          : error.message;

        const errors_message =
          error.response?.data?.errors?.[0]?.message ?? null;

        store.commit("portal/setShowLoading", false);
        store.commit("portal/setToast", error_message ?? errors_message);
      });
  },
  recuperarSenha(email, store) {
    store.commit("portal/setShowLoading", true);

    return Axios.post(api.recuperarSenha, { email: email })
      .then(() => {
        store.commit(
          "portal/setToast",
          `E-mail de recuperação de senha enviado para ${email}`
        );
      })
      .catch(({ response }) => {
        const status = response.status;

        let error_message = null;

        if (!status) {
          error_message = response;
        } else {
          error_message = response.data.message;
        }

        store.commit("portal/setToast", error_message);
      })
      .finally(() => {
        store.commit("portal/setShowLoading", false);
      });
  },
  redefinirSenha(data, store, router) {
    store.commit("portal/setShowLoading", true);

    return Axios.post(api.redefinirSenha, {
      email: data.email,
      senhaAtual: data.senhaAtual,
      senha: data.novaSenha
    })
      .then(() => {
        store.commit("portal/setToast", "Senha redefinida com sucesso");
        router.push({ name: "acessar" });
      })
      .catch(({ response }) => {
        const status = response.status;

        let error_message = null;

        if (!status) {
          error_message = response;
        } else {
          error_message = response.data.message;
        }

        store.commit("portal/setToast", error_message);
      })
      .finally(() => {
        store.commit("portal/setShowLoading", false);
      });
  }
};
